// @LTG-ID: data/journals/sba-financing-business-sale
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 26 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Deal Process (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "sba-financing-business-sale",
    FRONTMATTER: {
        number: "63",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "SBA Loans for Business Buyers: What Sellers Need to Know",
        deck: "SBA 7(a) loans are the most common financing for small business acquisitions in the United States, offering buyers long repayment terms with a government guarantee to the lender.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-08",
        publishedHuman: "June 8, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 475,
        timeRequired: "PT3M",
        keywords: "SBA loan to buy a business, SBA 7a business acquisition, SBA financing business sale, selling to an SBA buyer",
        articleSection: "Deal Process",
        articleBodyPreview: "SBA 7 a loans are the most common financing for small business acquisitions in the United States, offering buyers long repayment terms with a government guarantee to the lender. For sellers, an SBA financed buyer means more cash at closing but adds lender underwriting, documentation demands, and time to the process. Most individual buyers of small businesses do not have the purchase price in cash. The bridge between willing buyers and sellable businesses is very often a government guaranteed ban",
        complianceNote: "This article is educational and does not constitute lending, legal, or tax advice. SBA program terms change; verify current rules at SBA.gov.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "clean-financial-records-business-value",
        "seller-financing-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "SBA Loans for Business Buyers: What Sellers Need to Know",
        canonical: "https://letstalkgrowth.com/journals/sba-financing-business-sale",
        ogImage: "/journals/og/sba-financing-business-sale.jpg",
    },
    BODY_MARKDOWN: `SBA 7(a) loans are the most common financing for small business acquisitions in the United States, offering buyers long repayment terms with a government guarantee to the lender. For sellers, an SBA-financed buyer means more cash at closing but adds lender underwriting, documentation demands, and time to the process.

Most individual buyers of small businesses do not have the purchase price in cash. The bridge between willing buyers and sellable businesses is very often a government-guaranteed bank loan, and sellers who understand how it works run smoother sales.

## The program in brief

The Small Business Administration's 7(a) program guarantees a large portion of qualifying bank loans, which persuades lenders to finance acquisitions they would otherwise decline: loans to first-time owners, secured substantially by the cash flow of the business being bought. Program parameters, maximum loan amounts, guarantee percentages, required buyer equity injections, and eligibility rules, are set by SBA and updated periodically, so the current details should always be confirmed at SBA.gov or with an SBA lender; the durable facts are that terms run long, often around ten years for business acquisitions, and that the program finances a very large share of Main Street purchases.

## Why sellers should care about the buyer's loan

Because the lender is effectively a second buyer, underwriting your business with a banker's eyes:

- **Cash flow is king.** The lender must see historical earnings, on tax returns, not just internal statements, sufficient to service the debt with a margin. Businesses whose returns understate true earnings collide with this hard; the add-backs a buyer might accept informally must be documented to a standard a credit committee accepts.
- **Records get the full exam.** Years of tax returns, financials, and supporting detail. Everything in our financial records article applies double in an SBA deal.
- **Third-party valuation.** SBA deals typically require an independent business valuation ordered through the lender, one more reason sensible pricing survives the process and aspirational pricing dies in it.
- **The clock runs longer.** Underwriting, appraisal, and closing conditions add weeks to a timeline, and the deal's momentum must survive them.

There is also a structural point sellers discover late: where a seller note is part of an SBA-financed deal, the SBA and lender rules govern how that note ranks and when it can be paid, and those rules constrain the note's terms. An advisor who works with SBA deals regularly will structure around this from the start.

## The seller's preparation checklist for SBA-readiness

Tax returns that support the earnings story. Clean, reconciled financials. Documented add-backs. Sensible pricing a third-party appraiser can reach. And patience built into the timeline. A business prepared this way is not just SBA-financeable; it is more sellable to every buyer, because the SBA standard is really just the diligence standard with a credit committee attached.`,
    FAQ: [
        { question: "How much do buyers put down on an SBA acquisition loan?", answer: "SBA rules require a minimum equity injection from the buyer, with the specific percentage set by current program rules and lender policy; part of it can sometimes be structured via a seller note under conditions the rules define. Confirm current requirements with an SBA lender." },
        { question: "Does the SBA loan process slow down a sale?", answer: "It adds underwriting, an independent valuation, and closing conditions, typically weeks of additional timeline versus a cash deal. Sellers with organized records and realistic pricing move through it fastest." },
        { question: "Why do SBA lenders care about my tax returns instead of my P&L?", answer: "The guarantee program requires lenders to verify earnings against filed returns. Earnings that exist only on internal statements, or in unreported cash, cannot support the loan, which directly reduces what a financed buyer can pay." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Value Drivers",
            title: "Why Clean Financial Records Are Worth Real Money When You Sell",
            meta: "3 min · Brief 51",
            to: "/journals/clean-financial-records-business-value",
        },
        {
            tag: "Deal Process",
            title: "Seller Financing: How It Works and When It Makes Sense",
            meta: "3 min · Brief 61",
            to: "/journals/seller-financing-business-sale",
        },
    ],
};

export default BRIEF;
